/**
 * VoiceLabPanel Component
 * Main container for Voice Lab tab - coordinates all voice-related features
 */

import React from 'react';
import { VoiceProviderTabs } from './VoiceProviderTabs';
import { VoiceSelector } from './VoiceSelector';
import { VoiceSettings } from './VoiceSettings';
import { VoiceTestControls } from './VoiceTestControls';
import { VoiceRecorder } from './VoiceRecorder';
import { useVoiceManagement } from '../../hooks/useVoiceManagement';
import type { VoiceProvider, Voice } from '../../types/voice.types';

interface VoiceLabPanelProps {
  assistantId?: string;
  onVoiceChange?: (voice: Voice) => void;
  className?: string;
}

export const VoiceLabPanel: React.FC<VoiceLabPanelProps> = ({
  assistantId,
  onVoiceChange,
  className = ''
}) => {
  const {
    voices,
    selectedVoice,
    activeProvider,
    voiceSettings,
    isTestingVoice,
    isRecording,
    recordingDuration,
    transcriptionResult,
    loading,
    setActiveProvider,
    selectVoice,
    updateVoiceSettings,
    testVoice,
    startRecording,
    stopRecording,
    transcribeAudio,
    loadVoices
  } = useVoiceManagement();

  // Load voices on mount
  React.useEffect(() => {
    loadVoices();
  }, [loadVoices]);

  // Notify parent of voice changes
  React.useEffect(() => {
    if (selectedVoice && onVoiceChange) {
      onVoiceChange(selectedVoice);
    }
  }, [selectedVoice, onVoiceChange]);

  const handleProviderChange = (provider: VoiceProvider) => {
    setActiveProvider(provider);
  };

  const handleVoiceSelect = (voice: Voice) => {
    selectVoice(voice);
  };

  const handleTestVoice = (text: string) => {
    if (!selectedVoice) {
      return;
    }
    testVoice(text);
  };

  const handleTranscriptionTest = async () => {
    console.log('🟢 handleTranscriptionTest called, isRecording:', isRecording);
    if (!isRecording) {
      console.log('🟢 Starting recording...');
      await startRecording();
      console.log('🟢 After startRecording, isRecording:', isRecording);
    } else {
      console.log('🟢 Stopping recording...');
      const audioBlob = await stopRecording();
      console.log('🟢 Recording stopped, blob:', audioBlob);
      if (audioBlob) {
        console.log('🟢 Transcribing audio...');
        const result = await transcribeAudio(audioBlob);
        console.log('🟢 Transcription result:', result);
      }
    }
  };

  // Debug: Log isRecording changes
  React.useEffect(() => {
    console.log('🟢 VoiceLabPanel: isRecording changed to:', isRecording);
  }, [isRecording]);

  return (
    <div className={`flex-1 flex flex-col gap-6 p-6 ${className}`}>
      {/* Voice Provider Selection */}
      <VoiceProviderTabs
        activeProvider={activeProvider}
        onChange={handleProviderChange}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Voice Selection */}
        <div className="space-y-6">
          <VoiceSelector
            voices={voices[activeProvider] || []}
            selectedVoice={selectedVoice}
            onSelect={handleVoiceSelect}
            loading={loading}
            provider={activeProvider}
          />

          <VoiceSettings
            settings={voiceSettings}
            onChange={updateVoiceSettings}
            provider={activeProvider}
          />
        </div>

        {/* Right Column: Testing & Recording */}
        <div className="space-y-6">
          <VoiceTestControls
            onTest={handleTestVoice}
            isTesting={isTestingVoice}
            disabled={!selectedVoice}
            selectedVoice={selectedVoice}
          />

          <VoiceRecorder
            isRecording={isRecording}
            duration={recordingDuration}
            transcription={transcriptionResult?.text || null}
            onToggleRecording={handleTranscriptionTest}
            disabled={!selectedVoice}
          />
        </div>
      </div>

      {/* Info Banner */}
      {!selectedVoice && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="text-blue-400 text-sm">
            Select a voice from the list above to test text-to-speech and speech-to-text capabilities
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceLabPanel;
